import { fetchJson } from "@/services/api-client";
import { Product, ValidatedPromotionCode } from "@/types/subscription";

export type { ValidatedPromotionCode };

/**
 * Validates a promotion code and returns its details if valid
 * @param code The promotion code string entered by the user
 */
export async function validatePromotionCode(
  code: string,
): Promise<ValidatedPromotionCode> {
  return fetchJson<ValidatedPromotionCode>(
    `/api/stripe/promotion-code?code=${encodeURIComponent(code)}`,
    {
      method: "GET",
    },
    { fallbackErrorMessage: "Invalid promotion code" },
  );
}

/**
 * Creates a subscription for the user (customer) in Stripe
 * @param organizationId The organization ID
 * @param customerId The customer ID in Stripe
 * @param priceId The price ID in Stripe
 * @param quantity The quantity of the subscription
 * @param promotionCodeId Optional promotion code ID to apply a discount
 */
export async function createSubscription(
  organizationId: string,
  customerId: string,
  product: Product,
  priceId: string,
  quantity: number,
  promotionCodeId?: string,
): Promise<any | null> {
  if (!customerId || customerId === "UNASSIGNED") return null;

  const { subscription } = await fetchJson<{ subscription: any }>(
    "/api/stripe/subscription",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        organizationId,
        customerId,
        priceId,
        quantity,
        ...(promotionCodeId && { promotionCodeId }),
      }),
    },
    { fallbackErrorMessage: "Failed to create subscription" },
  );

  return subscription;
}

/**
 * Gets the checkout URL for the user (customer) in Stripe
 * @param organizationId The organization ID
 * @param customerId The customer ID in Stripe
 * @param priceId The price ID in Stripe
 */
export async function getCheckoutUrl(
  organizationId: string,
  customerId: string,
  product: Product,
  priceId: string,
): Promise<string> {
  const data = await fetchJson<{ url?: string }>(
    "/api/stripe/checkout",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        organizationId,
        customerId,
        product,
        priceId,
      }),
    },
    {
      fallbackErrorMessage:
        "Failed to create checkout session for subscription",
    },
  );

  return data.url || "";
}

/**
 * Creates a Stripe billing portal session for the user (customer)
 * @param organizationId The organization ID
 * @param customerId The customer ID in Stripe
 */
export async function getSubscriptionUrl(
  organizationId: string,
  customerId: string,
  flow?: {
    type: "subscription_cancel" | "subscription_update";
    subscriptionId: string;
  },
): Promise<string> {
  const returnUrl = `/settings/subscriptions`;

  const data = await fetchJson<{ url?: string }>(
    "/api/stripe/portal",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        organizationId,
        returnUrl,
        customerId,
        ...(flow ? { flow } : {}),
      }),
    },
    {
      fallbackErrorMessage: "Failed to create portal session for subscription",
    },
  );

  return data.url || "";
}

/**
 * Validates the payment link access for the user (customer)
 * @param organizationId The organization ID
 */
export async function validatePaymentLinkAccess(
  organizationId: string,
): Promise<void> {
  await fetchJson<unknown>(
    "/api/stripe/sessions",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ organizationId }),
    },
    { fallbackErrorMessage: "Failed to validate payment link access" },
  );
}
